import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import type { MailVarContents } from '@adornis/mails/db/mail-template.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { ComponentParamaterType, ComponentType, MessageType } from '@adornis/whatsapp/server/types.js';
import { DateTime } from 'luxon';
import { Institution } from '../entities/institution.js';
import {
  getWhatsappTemplate,
  InstitutionMailTemplate,
  MailTemplateType,
  MailTemplateVars,
} from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import { User } from '../entities/user.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { isDefined } from '../util.js';
import { getNotificationType, sendWhatsapp } from './_helper.js';

defineMailTemplate(MailTemplateType.NUTZER_TEILNAHME_AUFGEHOBEN, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Nutzer-Teilnahme aufgehoben',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>leider mussten wir Ihre Teilnahme an der Veranstaltung revidieren. Anbei sind die Details der Veranstaltung aufgef&uuml;hrt:</p>
<p>{{changed-registrations}}</p>
<p>Sollte es sich hier um einen Fehler handeln, wenn Sie sich bei uns per Mail.</p>
<p>Wir freuen uns, wenn wir Sie in Zukunft in einer unserer Veranstaltungen begr&uuml;&szlig;en d&uuml;rfen und empfehlen Ihnen hierf&uuml;r unser Vortrags- und Workshop-Programm auf <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de.</a></p>
<p>------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>You have been deregisterd from the following event:</p>
<p>{{changed-registrations}}</p>
<p>The cancellation refers only to the listed event. If we have made a mistake, please contact us by e-mail.</p>
<p>You can finde a list of your event registrations for the current semester under the following link: {{login-link}}</p>
<p>We look forward to welcoming you to one of our events in the future and recommend our lecture and workshop program at anmeldung.uni-frankfurt.de for this purpose</p>
<p>Best regards</p>`,
    footer: '',
  },
  variables: InstitutionMailTemplate.getAvailableVariables(MailTemplateType.NUTZER_TEILNAHME_AUFGEHOBEN),
  attachments: [],
  displayName: 'Teilnahme aufgehoben',
  editorHint: () => InstitutionMailTemplate.getMailHint(MailTemplateType.NUTZER_TEILNAHME_AUFGEHOBEN),
});

export async function notifyNuzterTeilnahmeAufgehoben(participationID: string) {
  const participation = await getByID(
    Participation,
    participationID,
  )({
    _id: 1,
    _class: 1,
    userID: 1,
    productID: 1,
    state: 1,
    waitingListPlace: 1,
    product: {
      _id: 1,
      name: 1,
      institutionID: 1,
      appointments: { _id: 1, start: 1, end: 1, location: 1, name: 1, meetingsLink: { link: 1 } },
    },
    user: { _id: 1, title: 1, firstName: 1, lastName: 1, email: 1, whatsappNotification: 1, whatsappPhone: 1 },
  });
  if (!participation) throw new Error('Couldnt find participation with ID ' + participationID);

  const user = participation.user;
  if (!user) throw new Error('no user is set');

  const institutionID = participation.product?.institutionID;
  const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
    institutionID,
    MailTemplateType.NUTZER_TEILNAHME_AUFGEHOBEN,
  )(InstitutionMailTemplate.allFields);

  const institution = await getByID(Institution, institutionID)({ name: 1 });

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const start = participation.product?.appointments?.[0]?.start;
  const daysTillEvent = start?.diff(DateTime.now()).as('days').toFixed(0);
  let dates = participation.product?.appointments
    ?.map(a => a.start?.toFormat('dd.LL.yyyy'))
    .filter(isDefined)
    .join(', ');
  if (!dates) dates = start.toFormat('dd.LL.yyyy');

  const notificationType = getNotificationType(user, mailTemplate);
  if (notificationType === 'whatsapp') {
    await sendWhatsapp({
      to: user.whatsappPhone!,
      type: MessageType.TEMPLATE,
      content: {
        template: {
          name: getWhatsappTemplate(MailTemplateType.NUTZER_TEILNAHME_AUFGEHOBEN).name,
          language: {
            code: 'de',
          },
          components: [
            {
              type: ComponentType.BODY,
              parameters: [
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapFirstNameVar(user) || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapLastNameVar(user) || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text: participation.product?.name || '-',
                },
              ],
            },
          ],
        },
      },
    });
  } else if (notificationType === 'email') {
    const mailVars = {
      [MailTemplateVars.EVENT_TITLE]: participation.product?.name ?? '',
      [MailTemplateVars.EVENT_DATES]: dates,
      [MailTemplateVars.EVENT_LEAD_DAYS]: daysTillEvent,
      [MailTemplateVars.TITLE]: participation.user?.title ?? '',
      [MailTemplateVars.FIRST_NAME]: participation.user?.firstName,
      [MailTemplateVars.LAST_NAME]: participation.user?.lastName,
      [MailTemplateVars.EVENT_TITLE]: participation.product?.name,
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
      [MailTemplateVars.CHANGED_REGISTRATIONS]: [
        InstitutionMailTemplate.mapChangedRegistrationVar({
          productBefore: participation.product.toJSON(),
          newState: participation.state,
          newWaitingListPlace: participation instanceof ParticipationWorkshop ? participation.waitingListPlace : null,
          participation,
          institution,
          englishVersion: false,
        }),
      ],
      [MailTemplateVars.CHANGED_REGISTRATIONS_EN]: [
        InstitutionMailTemplate.mapChangedRegistrationVar({
          productBefore: participation.product.toJSON(),
          newState: participation.state,
          newWaitingListPlace: participation instanceof ParticipationWorkshop ? participation.waitingListPlace : null,
          participation,
          institution,
          englishVersion: true,
        }),
      ],
    } as MailVarContents;

    const content = await mailTemplate.buildEmail(mailVars as any);
    const subject = await mailTemplate.buildSubject(mailVars as any);

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');
    const fullEmailRec = {
      email: user.email,
      institutionID,
      content,
      subject,
    };

    await sendInstitutionEmail(fullEmailRec);
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}
